import Drawer from '@/components/drawers';
import DrawerContent from '@/components/drawers/content';
import DrawerHeader from '@/components/drawers/header';
import DrawerButtons from '@/components/form/drawer-buttons';
import InputControl from '@/components/form/input-control';
import useFormHandle from '@/hooks/use-form-handle.hook';
import useModal from '@/hooks/use-modal.hook';
import useQueryHelper from '@/hooks/use-query-helper';
import useToast from '@/hooks/use-toast.hook';
import { UiOption } from '@/lib/helpers';
import useViewMode from '@/hooks/use-view-mode.hook';
import { PrivateModelModel } from '@/lib/models/private-model/private-model.model';
import {
  createPrivateModel,
  deletePrivateModel,
  getModelUsage,
  getPrivateModelById,
  getProviderModels,
  ModelUsage,
  ModelUsageMap,
  patchPrivateModel,
} from '@/lib/services/private-model.service';
import { Stack } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import { Form } from '@/components/form';
import { getPrivateModelConnectors } from '@/lib/services/connector.service';
import InlineToast from '@/components/toasts/inline';
import useFeature from '@/hooks/use-feature.hook';
import { getTypedConnector } from '@/lib/models/connector';
import { CreateResponse, PatchResponse } from '@/lib/services';
import { DrawerProps } from '../helpers';
import MoreMenu from '@/components/more-menu';
import UpdateMenuItems from '@/components/more-menu/update-menu-items';
import { pick, sortBy } from 'lodash';
import RouterTabs from '@/components/router-tabs';
import { TabPanel } from '@/components/router-tabs/helpers';
import PrivateModelDetails from './details';
import PrivateModelUsage from './usage';
import AzureOpenAIPrivateModel from './azure-openai-private-model';
import AwsPrivateModel from './aws-private-model';
import { AwsConnectorModel } from '@/lib/models/connector/aws-connector.model';
import VertexPrivateModel from './vertex-private-model';
import { ProviderModel } from '@/lib/models/provider-model.model';

interface Props extends DrawerProps {
  id: string;
  defaultConnectorId?: string;
  defaultModelId?: string;
}
const formModel = new PrivateModelModel();

const getModelOpts = (providerModels: ProviderModel[]): UiOption[] => {
  if (!providerModels?.length) {
    return [];
  }

  const makeModelOpt = (model: ProviderModel, disabled = false): UiOption => ({
    value: model.modelId,
    label: `${model.name} (${model.modelId})`,
    disabled,
  });

  const supportedModelOpts = sortBy(
    providerModels.map((model) => makeModelOpt(model)),
    ({ label }: UiOption) => String(label).toLowerCase()
  );

  return [
    { label: 'Supported Models', value: 'supported', meta: { header: true } },
    ...supportedModelOpts,
  ];
};

export const PrivateModelDrawer: FC<Props> = ({
  id,
  open,
  onClose,
  isTopDrawer,
  className,
  defaultConnectorId = '',
  defaultModelId = '',
}) => {
  const [modelId, setModelId] = useState(id || null);
  const isNew = !modelId;
  const { mode, setMode, inWriteMode } = useViewMode(isNew ? 'edit' : 'view');
  const { toast, errorToast } = useToast();
  const { openModal } = useModal();

  const { canChangePrivateModels } = useFeature();

  const query = useQuery<[PrivateModelModel | null, ModelUsageMap]>(
    [QueryKey.PrivateModelViewMeta, modelId],
    async () => Promise.all([getPrivateModelById(modelId!), getModelUsage([modelId!])]),
    {
      enabled: !!modelId,
    }
  );

  const { showLoader: modelLoading } = useQueryHelper(query);
  const { data, refetch } = query;

  const [modelData, usageData] = data || [null, new Map<string, ModelUsage>()];
  const privateModel = modelData || new PrivateModelModel();

  const handleRefresh = () => {
    refetchQuery(QueryKey.PrivateModelsList);
  };

  const formHandle = useFormHandle(
    {
      initialValues: new PrivateModelModel({
        connectorId: defaultConnectorId,
        modelId: defaultModelId,
      }),
      validationSchema: formModel.schema,
      validateOnChange: !isNew,
      onSubmit: async (values, { resetForm, setFieldError }) => {
        const handleErrors = (response: CreateResponse | PatchResponse): boolean => {
          const { error, fieldErrors } = response;

          if (error) {
            errorToast(error);
            return false;
          }

          if (fieldErrors) {
            fieldErrors.forEach(({ path, msg }) => {
              setFieldError(path, msg);
            });
            return false;
          }

          return true;
        };

        if (!modelId) {
          const response = await createPrivateModel(values);

          if (!handleErrors(response)) {
            return;
          }

          const newModel = response.model as PrivateModelModel;

          resetForm({ values });
          toast('The private model was created');
          setModelId(newModel.id);
          handleRefresh();
          setMode('view');
          return;
        }

        const response = await patchPrivateModel(
          values.id,
          pick(values, ['name', 'config', 'provider', 'modelId'])
        );

        if (!handleErrors(response)) {
          return;
        }

        resetForm({ values });
        toast('The private model was updated');
        refetch();
        handleRefresh();
        setMode('view');
      },
    },
    { lockTarget: 'drawer' }
  );

  const { handleSubmit, canSubmit, isSubmitting, loadData, values, setFieldValue } = formHandle;
  const connectorId = isNew ? values.connectorId : privateModel.connectorId;

  const connectorQuery = useQuery<ConnectorModel[]>([QueryKey.ConnectorModelsList], async () =>
    getPrivateModelConnectors()
  );

  const { data: availableConnectors = [] } = connectorQuery;
  const { showLoader: connectorsLoading } = useQueryHelper(connectorQuery);

  const modelConnector = useMemo(() => {
    const connector = availableConnectors.find(({ id }) => id === connectorId);

    if (!connector) {
      return new ConnectorModel();
    }

    return getTypedConnector(connector);
  }, [availableConnectors, connectorId]);

  const providerModelsQuery = useQuery<ProviderModel[]>(
    [QueryKey.ProviderModelsList, modelConnector.id],
    async () => {
      if (!modelConnector.id || !modelConnector.canListProviderModels) {
        return [];
      }

      return (await getProviderModels([modelConnector.id])).rows;
    },
    { enabled: !!modelConnector.id && modelConnector.canListProviderModels }
  );

  const { data: providerModels = [] } = providerModelsQuery;
  const { showLoader: providerModelsLoading } = useQueryHelper(providerModelsQuery);

  let modelUsage: ModelUsage = { policies: [], agents: [], inUse: false };
  if (modelId && usageData?.has(modelId)) {
    modelUsage = usageData.get(modelId)!;
  }

  const handleCancel = () => {
    if (isNew) {
      onClose(true);
      return;
    }
    setMode('view');
    loadData(privateModel);
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleClose = () => {
    onClose();
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: `Are you sure you want to delete ${privateModel.name}?`,
      onClose: (confirm: boolean) => {
        if (confirm) {
          deletePrivateModel(modelId!).then(({ deleted, error }) => {
            if (deleted) {
              setModelId('');
              onClose();
              toast('The model was deleted');
              handleRefresh();
              return;
            }

            errorToast(error || 'The model was not deleted');
          });
        }
      },
    });
  };

  const handleChangeConnector = () => {
    setFieldValue('modelId', '');
    setFieldValue('provider', '');
    setFieldValue('config', {});
  };

  const handleChangeModelId = (modelId: string) => {
    const providerModel = providerModels.find(
      ({ modelId: providerModelId }) => providerModelId === modelId
    );

    if (providerModel) {
      setFieldValue('provider', providerModel.provider, false);
    }
  };

  const handleTest = () => {
    refetch();
    handleRefresh();
  };

  const connectorOpts: UiOption[] = availableConnectors.map(({ id, name, status }) => ({
    label: name,
    value: id,
    disabled: status !== 'active',
  }));

  const hasConnector = !!modelConnector.id;
  const hasUsage = modelUsage.inUse;
  const canDelete = !hasUsage;
  const showModelSelect = modelConnector.canListProviderModels;
  const connectorType = modelConnector?.type || '';

  const modelOpts: UiOption[] =
    hasConnector && !providerModelsLoading ? getModelOpts(providerModels) : [];
  const hasModelOpts = !!modelOpts.filter(({ meta }) => !meta?.header).length;
  const noModelsFound = hasConnector && !providerModelsLoading && !hasModelOpts;
  const modelNotFound =
    !isNew &&
    hasConnector &&
    values.modelId &&
    !modelOpts.some(({ value }) => values.modelId === value);

  const panels: TabPanel[] = [
    {
      label: 'Details',
      icon: 'info-circle',
      path: 'details',
      component: (
        <PrivateModelDetails model={privateModel} connector={modelConnector} onTest={handleTest} />
      ),
    },
    {
      label: 'Usage',
      icon: 'policy',
      path: 'usage',
      component: <PrivateModelUsage usage={modelUsage} />,
      disabled: isNew,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      className={className}
      loading={connectorsLoading || modelLoading}
      size="medium"
    >
      <DrawerHeader
        onClose={handleClose}
        isTopDrawer={isTopDrawer}
        menu={
          <MoreMenu menuId="private-model-menu">
            {!isNew && canChangePrivateModels && (
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={!inWriteMode}
                canDelete={canDelete}
                editSubtitle={inWriteMode ? 'You cannot edit a model that is in use' : ''}
                deleteSubtitle={!canDelete ? 'You cannot delete a model that is in use' : ''}
              />
            )}
          </MoreMenu>
        }
      >
        {isNew ? 'Add Private Model' : privateModel.name}
      </DrawerHeader>

      <DrawerContent>
        {!inWriteMode ? (
          <RouterTabs variant="drawer" panels={panels} />
        ) : (
          <Stack mt={2}>
            <Form formHandle={formHandle} query={query}>
              <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

              <InputControl
                name="connectorId"
                label="Connector"
                formHandle={formHandle}
                onChange={handleChangeConnector}
                type="select"
                options={connectorOpts}
                readonly={!isNew}
                loading={connectorsLoading}
              />

              {connectorType === 'aws' && (
                <AwsPrivateModel
                  formHandle={formHandle}
                  mode={mode}
                  connector={modelConnector as AwsConnectorModel}
                />
              )}

              {connectorType === 'vertex-ai' && (
                <VertexPrivateModel formHandle={formHandle} mode={mode} />
              )}

              {showModelSelect && (
                <>
                  <InputControl
                    name="modelId"
                    label="Model"
                    formHandle={formHandle}
                    type="select-search"
                    options={modelOpts}
                    disabled={!hasConnector || noModelsFound}
                    onChange={handleChangeModelId}
                    loading={providerModelsLoading}
                  />

                  {noModelsFound && (
                    <InlineToast level="warning" message="No models found for this connector" />
                  )}

                  {modelNotFound && !noModelsFound && (
                    <InlineToast
                      level="warning"
                      message="The model was not found for this connector. Please run an Inference Test for more details."
                    />
                  )}
                </>
              )}

              {connectorType === 'azure-openai' && (
                <AzureOpenAIPrivateModel formHandle={formHandle} mode={mode} />
              )}

              <DrawerButtons
                submit
                cancel
                isNew={isNew}
                canSubmit={canSubmit}
                canCancel={!isSubmitting}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                formHandle={formHandle}
              />
            </Form>
          </Stack>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default PrivateModelDrawer;
